import { useState, useEffect, useContext, useRef } from "react";
import {
  Search, MessageSquare, ArrowLeft, Send, RotateCcw,
  User, Wrench, Shield, X, CheckCircle2,
  ListChecks, Clock, BarChart3
} from "lucide-react";
import axios from "axios";
import AppContext from "../../../context/AppContext";
import SupStatusBadge from "../../../components/general/SupportSharedFiles/SupStatusBadge";
import SupEmptyState from "../../../components/general/SupportSharedFiles/SupEmptyState";
import SupLoader from "../../../components/general/SupportSharedFiles/SupLoader";
import "../../../assets/css/admin/adminTicketManagement/admsupporttickets.css";
import "../../../assets/css/general/Support/supshare.css";

const FILTERS = ["all", "open", "in_progress", "resolved"];

const AdmSupportTickets = () => {
  const { baseUrl, authToken } = useContext(AppContext);
  const bottomRef = useRef(null);

  const [tickets, setTickets] = useState([]);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState("");

  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0 });

  const [activeChatId, setActiveChatId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [chatLoading, setChatLoading] = useState(false);
  const [reply, setReply] = useState("");
  const [sending, setSending] = useState(false);

  const fetchTickets = () => {
    setListLoading(true);
    axios
      .get(`${baseUrl}/admin/support-tickets`, {
        headers: { Authorization: `Bearer ${authToken}` },
        params: { status: filter },
      })
      .then((res) => {
        const all = res.data.supporttickets || [];
        setTickets(all);
        setStats({
          total:       all.length,
          open:        all.filter((t) => t.status === "open").length,
          in_progress: all.filter((t) => t.status === "in_progress").length,
          resolved:    all.filter((t) => t.status === "resolved").length,
        });
        setListError("");
      })
      .catch((err) => {
        console.error(err.response?.data || err.message);
        setListError("Failed to load support tickets.");
      })
      .finally(() => setListLoading(false));
  };

  const openChat = (ticket) => {
    setActiveChatId(ticket.id);
    setChatLoading(true);
    setMessages([]);

    axios
      .get(`${baseUrl}/admin/support-tickets/${ticket.id}/messages`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((res) => {
        setMessages(res.data.messages || []);
        setTickets((prev) =>
          prev.map((t) =>
            t.id === ticket.id && t.status === "open"
              ? { ...t, status: "in_progress" }
              : t
          )
        );
      })
      .catch((err) => console.error(err.response?.data || err.message))
      .finally(() => setChatLoading(false));
  };

  const sendReply = () => {
    if (!reply.trim()) return;
    setSending(true);

    axios
      .post(
        `${baseUrl}/admin/support-tickets/${activeChatId}/reply`,
        { message: reply },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then((res) => {
        setMessages((prev) => [...prev, res.data.chat]);
        setReply("");
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to send message.");
      })
      .finally(() => setSending(false));
  };

  const resolveTicket = (favourIn) => {
    axios
      .post(
        `${baseUrl}/admin/support-tickets/${activeChatId}/resolve`,
        { favour_in: favourIn },
        { headers: { Authorization: `Bearer ${authToken}` } }
      )
      .then(() => {
        setTickets((prev) =>
          prev.map((t) =>
            t.id === activeChatId
              ? { ...t, status: "resolved", favour_in: favourIn }
              : t
          )
        );
      })
      .catch((err) => {
        console.error(err);
        alert("Failed to resolve ticket.");
      });
  };

  useEffect(() => { fetchTickets(); }, [filter, authToken]);

  useEffect(() => {
    if (!activeChatId) return;
    const poll = () => {
      axios
        .get(`${baseUrl}/admin/support-tickets/${activeChatId}/messages`, {
          headers: { Authorization: `Bearer ${authToken}` },
        })
        .then((res) => setMessages(res.data.messages || []))
        .catch(() => {});
    };
    const interval = setInterval(poll, 5000);
    return () => clearInterval(interval);
  }, [activeChatId, authToken]);

  useEffect(() => {
    if (activeChatId) bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, activeChatId]);

  const filtered = tickets.filter((t) => {
    const q = search.toLowerCase();
    return (
      t.category?.toLowerCase().includes(q) ||
      t.raisedby?.toLowerCase().includes(q) ||
      t.against?.toLowerCase().includes(q) ||
      String(t.id).includes(q)
    );
  });

  const activeTicket = tickets.find((t) => t.id === activeChatId);

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    return new Date(dateStr).toLocaleTimeString("en-PK", { hour: "2-digit", minute: "2-digit" });
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-PK", {
      day: "2-digit", month: "short", year: "numeric",
    });
  };

  const getSenderAvatar = (sender) => {
    if (sender === "admin")  return <Shield size={13} />;
    if (sender === "vendor") return <Wrench size={13} />;
    return <User size={13} />;
  };

  if (activeChatId && activeTicket) {

    const isHotelTicket = activeTicket.against === "vendor";

    return (
      <div className="adm-st-page">
        <div className="adm-st-chat">

          <div className="adm-st-chat__header">
            <div className="adm-st-chat__header-left">
              <button
                className="adm-st-back-btn"
                onClick={() => { setActiveChatId(null); fetchTickets(); }}
              >
                <ArrowLeft size={16} />
              </button>
              <div>
                <p className="adm-st-chat__title">{activeTicket.category}</p>
                <div className="adm-st-chat__meta">
                  <span className="adm-st-chat__id">#{activeTicket.id}</span>
                  <span>·</span>
                  <span>By: {activeTicket.raisedby}</span>
                  <span>·</span>
                  {isHotelTicket ? (
                    <span>Against: vendor</span>
                  ) : (
                    <span className="adm-st-chat__pkg-tag">📦 Package Ticket</span>
                  )}
                  <SupStatusBadge status={activeTicket.status} />
                </div>
              </div>
            </div>

            {activeTicket.status !== "resolved" && (
              <div className="adm-st-resolve-btns">
                {isHotelTicket && (
                  <button
                    className="adm-st-resolve-btn adm-st-resolve-btn--vendor"
                    onClick={() => resolveTicket("vendor")}
                  >
                    <Wrench size={12} /> Favor Vendor
                  </button>
                )}
                <button
                  className="adm-st-resolve-btn adm-st-resolve-btn--customer"
                  onClick={() => resolveTicket("customer")}
                >
                  <User size={12} /> Favor Customer
                </button>
              </div>
            )}

            {/* Resolved pill */}
            {activeTicket.status === "resolved" && (
              <div className="adm-st-resolved-pill">
                <CheckCircle2 size={14} /> Resolved · {activeTicket.favour_in}
              </div>
            )}
          </div>

          {/* Messages Area */}
          <div className="adm-st-chat__messages">
            {chatLoading && <SupLoader label="Loading conversation..." />}

            {!chatLoading && messages.length === 0 && (
              <div className="adm-st-chat__empty">No messages yet.</div>
            )}

            {!chatLoading && messages.map((msg, idx) => (
              <div
                key={msg.id || idx}
                className={`adm-st-msg ${
                  msg.sender === "admin"
                    ? "adm-st-msg--admin"
                    : msg.sender === "vendor"
                    ? "adm-st-msg--vendor"
                    : "adm-st-msg--customer"
                }`}
              >
                {msg.sender !== "admin" && (
                  <div className={`adm-st-msg__avatar adm-st-msg__avatar--${msg.sender}`}>
                    {getSenderAvatar(msg.sender)}
                  </div>
                )}
                <div className="adm-st-msg__bubble">
                  <p className="adm-st-msg__sender">{msg.sender}</p>
                  {msg.message && <p className="adm-st-msg__text">{msg.message}</p>}
                  <span className="adm-st-msg__time">{formatTime(msg.created_at)}</span>
                </div>
              </div>
            ))}
            <div ref={bottomRef} />
          </div>

          {/* Reply Footer */}
          {activeTicket.status !== "resolved" && (
            <div className="adm-st-chat__footer">
              <div className="adm-st-chat__input-row">
                <input
                  className="adm-st-reply-input"
                  value={reply}
                  onChange={(e) => setReply(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && sendReply()}
                  placeholder="Type your reply..."
                />
                <button
                  className="adm-st-send-btn"
                  onClick={sendReply}
                  disabled={sending || !reply.trim()}
                >
                  <Send size={14} />
                </button>
              </div>
            </div>
          )}

          {activeTicket.status === "resolved" && (
            <div className="adm-st-resolved-footer">
              ✓ Ticket resolved · Favor: {activeTicket.favour_in}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="adm-st-page">

      {/* Header */}
      <div className="adm-st-header">
        <div>
          <h2 className="adm-st-heading">Support Tickets</h2>
          <p className="adm-st-subtext">Manage all user support requests</p>
        </div>
        <div className="adm-st-search-wrap">
          <Search size={14} className="adm-st-search-icon" />
          <input
            className="adm-st-search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search tickets, users..."
          />
        </div>
      </div>

      {listError && <div className="sup-error-banner">{listError}</div>}

      <div className="adm-st-stats">
        {[
          { label: "Total",       value: stats.total,       icon: ListChecks,   cls: "--total"    },
          { label: "Open",        value: stats.open,        icon: Clock,        cls: "--open"     },
          { label: "In Progress", value: stats.in_progress, icon: BarChart3,    cls: "--progress" },
          { label: "Resolved",    value: stats.resolved,    icon: CheckCircle2, cls: "--resolved" },
        ].map((s) => (
          <div key={s.label} className="adm-st-stat-card">
            <div className={`adm-st-stat-icon adm-st-stat-icon${s.cls}`}>
              <s.icon size={18} />
            </div>
            <div>
              <p className="adm-st-stat-value">{s.value}</p>
              <p className="adm-st-stat-label">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="adm-st-filters">
        {FILTERS.map((f) => (
          <button
            key={f}
            className={`adm-st-filter-btn ${filter === f ? "adm-st-filter-btn--active" : ""}`}
            onClick={() => setFilter(f)}
          >
            {f.replace("_", " ")}
          </button>
        ))}
        <button className="adm-st-refresh-btn" onClick={fetchTickets}>
          <RotateCcw size={13} />
        </button>
      </div>

      {listLoading ? (
        <SupLoader label="Loading tickets..." />
      ) : filtered.length === 0 ? (
        <SupEmptyState
          icon={MessageSquare}
          title="No tickets found"
          subtitle="Adjust your filters or search term"
        />
      ) : (
        <div className="adm-st-list">
          {filtered.map((ticket, i) => (
            <div
              key={ticket.id}
              className="adm-st-ticket"
              style={{ animationDelay: `${i * 0.04}s` }}
            >
              <div className="adm-st-ticket__left">
                <div className="adm-st-ticket__top">
                  <span className="adm-st-ticket__id">#{ticket.id}</span>
                  <SupStatusBadge status={ticket.status} />
                </div>
                <p className="adm-st-ticket__title">{ticket.category}</p>
                <p className="adm-st-ticket__desc">{ticket.description}</p>
                <div className="adm-st-ticket__footer">
                  <span>By: <strong>{ticket.raisedby}</strong></span>
                  <span className="adm-st-ticket__sep">·</span>
                  {ticket.against === "admin" ? (
                    <span className="adm-st-ticket__pkg-tag">📦 Package Ticket</span>
                  ) : (
                    <span>Against: <strong>{ticket.against}</strong></span>
                  )}
                  <span className="adm-st-ticket__sep">·</span>
                  <span className="adm-st-ticket__date">{formatDate(ticket.created_at)}</span>
                  {ticket.status === "resolved" && ticket.favour_in && (
                    <span className="adm-st-ticket__resolved-tag">
                      ✓ {ticket.favour_in}
                    </span>
                  )}
                </div>
              </div>
              <button className="adm-st-open-btn" onClick={() => openChat(ticket)}>
                <MessageSquare size={13} /> Open
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AdmSupportTickets;